import React, { useEffect, useState } from 'react';
import {
  TextField,
  ListProps,
  ReferenceField,
  Identifier,
  FunctionField,
  useRedirect,
  RowClickFunction,
  Link,
  useNotify,
} from 'react-admin';

import { cropHttp } from '@network/crop-http';
import {
  PhoneField,
  EmailField,
  TextInput,
  SelectInput,
  Grid,
  ImageField,
  CommentsField,
  BooleanField,
  Forbidden,
  SelectArrayInput,
  ButtonLinkField,
  NullableBooleanInput,
  DateField,
  MoneyField,
  DateRangeSelect,
} from '@components';
import { userStatusOptions, UserStatusField } from './UserStatusField';
import { axiosErrorToString, dateFormatter, formatHoursToPhrase, rbac } from '@utils';
import {
  dealerTiersChoices,
  genderOptions,
  userTypeOptions,
  LoginTry,
  loginTryOptions,
  marriageOptions,
  moffinOptions, SyntageProfile,
  User,
  userSourceOptions,
} from '@types';
import { adminHttp } from '@network';
import { UserCity } from './UserCity';
import { checkSuperadmin } from '@providers';
import { UserLoginBtn } from './UserLoginBtn';
import { stateHttp } from '@network/state-http';
import { UserListActions } from './UserListActions';
import { GridList } from '@components/GridList/GridList';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { checkRbacPermission } from '@utils/check-rbac-permission';
import { creditStatusBlankOptions } from '@pages/credit/CreditStatusField';
import { AutocompleteFilter } from '@components/filters/AutocompleteFilter';
import { profileProgressOptions, UserProfileProgress } from '@pages/user/UserProfileProgressField';
import cls from './UserList.module.css';
import { SyntageStatusField } from '@pages/user/SyntageStatusField';
import { SyntageProfileField } from '@pages/user/SyntageProfileField';

export const UserList = (props: ListProps) => {
  const isSuperadmin = checkSuperadmin();
  const exporter = useExporter({
    filename: 'Verqor Farmers',
    props: {
      'id': {
        label: 'ID',
      },
      'code': {
        label: 'Verqor Code',
      },
      'fullName': {
        label: 'Full name',
      },
      'isRenewal': {
        label: 'Eligible for Renewal',
        transform: (value: boolean) => value ? 'Yes' : 'No',
      },
      'lastLoginTry': {
        label: 'Login Try',
        prop: 'lastLoginTry',
        transform: (loginTry: LoginTry | null) => {
          if (!loginTry) return '';
          const loginAt = dateFormatter.toDateTime(loginTry.createdAt, true);
          const succeedText = loginTry.isAdmin ? 'Is succeed by admin' : 'Is succeed';
          return [
            loginAt,
            succeedText,
            loginTry.ip ? `IP: ${loginTry.ip}` : '',
          ].filter(Boolean).join(', ') + (loginTry.location ? ` | ${loginTry.location}` : '');
        },
      },
      'status': {
        label: 'User status',
        prop: 'statusEn',
      },
      'profileProgress': {
        label: 'Profile progress',
        transform: 'uppercase',
      },
      'inactiveReason': {
        label: 'Inactivity Reason',
      },
      'rejectReason': {
        label: 'Reason of rejection',
      },
      'phone': {
        label: 'Phone',
        transform: rbac.canShowUserPhone() ? 'phone' : 'masked',
      },
      'email': {
        label: 'Email',
      },
      'createdAt': {
        label: 'Registration Date',
        transform: 'date',
      },
      'lastStatusAt': {
        label: 'Last Status Change',
        transform: 'datetime',
      },
      'sourceEn': {
        label: 'Channel',
      },
      'gender': {
        label: 'Gender',
      },
      'birthday': {
        label: 'Birthday',
        transform: 'date',
      },
      'buyerNames': {
        label: 'Buyers',
      },
      'retailerNames': {
        label: 'Retailers',
      },
      'associateNames': {
        label: 'Associates',
      },
      'sfContactLink': {
        label: 'SalesForce Contact',
      },
      'sfAccountLink': {
        label: 'SalesForce Account',
      },
      'companyName': {
        label: 'Company',
      },
      'isCompany': {
        label: 'Is Company',
        transform: 'boolean',
      },
      'moffinBureauScore': {
        label: 'Bureau Score',
        transform: 'spaced',
      },
      'cropsNames': {
        label: 'Crops',
      },
      'latestCredit.code': {
        label: 'Latest Credit',
        prop: 'latestCredit.code',
      },
      'latestCredit.statusEn': {
        label: 'Latest Credit Status',
        prop: 'latestCredit.statusEn',
      },
      'latestCredit.requestAmount': {
        label: 'Last Requested Amount',
        prop: 'latestCredit.requestAmount',
        transform: 'moneyNullable',
      },
      'latestCredit.creditAmount': {
        label: 'Last Credit Amount',
        prop: 'latestCredit.creditAmount',
        transform: 'moneyNullable',
      },
      'comments': {
        label: 'Notes',
        transform: 'comments',
      },
      'mainBuyer.name': {
        label: 'Withholding Company',
        prop: 'mainBuyer.name',
      },
      'tier': {
        label: 'Withholding Company Tier',
        prop: 'tier',
      },
      'stateId': {
        label: 'State',
        prop: 'state.name',
      },
      'cityName': {
        label: 'City',
      },
      'rfc': {
        label: 'RFC',
      },
      'rfcCompany': {
        label: 'RFC (Company)',
      },
      'rfcConflictFields': {
        label: 'RFC Conflicts',
      },
      'marriage': {
        label: 'Civil status',
      },
      'address': {
        label: 'Address',
      },
      'city': {
        label: 'Municipality',
        prop: 'location',
        transform: (location: {city: string}[]) =>
          location.map(loc => loc.city).filter(Boolean).join(', '),
      },
      'activation.activation': {
        label: 'Activation',
        prop: 'activation.activation',
      },
      'responsibleAdminId': {
        label: 'Manager',
        prop: 'responsibleAdmin.name',
      },
      'responsibleAdmin.name': {
        label: 'Responsible manager',
      },
      'contactOwner.name': {
        label: 'Owner',
      },
      'hoursRegisterApplied': {
        label: 'Register to Applying',
        transform: (user: User) => formatHoursToPhrase(user?.hoursRegisterApplied),
      },
      'timerText': {
        label: 'Register to Applying',
      },
      'syntageProfile.credentialStatus': {
        label: 'CIEC Status',
        transform: 'uppercase',
      },
      'syntageProfile': {
        label: 'SAT Profile',
        transform: (syntageProfile: SyntageProfile) => syntageProfile ? 'Created' : 'Missing',
      },
      'syntageProfile.createdAt': {
        label: 'SAT Timestamp',
        transform: 'datetime',
      },
    },
  });

  const permissionUserLogin = checkRbacPermission('user/{id}/login-admin|ALL');
  const showLoginButton = isSuperadmin || permissionUserLogin;
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const redirect = useRedirect();
  const notify = useNotify();

  const [cropOptions, setCropOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [adminOptions, setAdminOptions] = useState<{id?: number, name: string}[]>([]);
  const [respManagerOptions, setRespManagerOptions] = useState<{id?: number, name: string}[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const crops = cropHttp.getAll();
        const states = stateHttp.getAll();
        const fetchOwners = adminHttp.getAll();
        const fetchResManagers = adminHttp.getResponsibleManagers();
        const [loadedCrops, loadedStates, owners, resManagers] = await Promise
          .all([crops, states, fetchOwners, fetchResManagers]);

        setCropOptions(loadedCrops
          .sort((a, b) => a.nameMx.localeCompare(b.nameMx))
          .map(crop => ({
            id: crop.id,
            name: crop.nameEn || crop.nameMx || `Crop id: ${crop.id}`,
          })));
        setStateOptions(loadedStates
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(state => ({
            id: state.id,
            name: state.name || state.name || `State id: ${state.id}`,
          })),
        );
        if (owners.length) {
          setAdminOptions(owners.map(o => ({ id: o.id, name: o.name })));
        }
        if (resManagers.length) {
          setRespManagerOptions(resManagers.map(o => ({ id: o.id, name: o.name })));
        }
      } catch (e) {
        notify(axiosErrorToString(e));
      }
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  const userRowClick: RowClickFunction = (id: Identifier, resource: string) => {
    redirect(`/${resource}/${id}?initial`);
    return false;
  };

  return (
    <GridList {...props}
      editComponent={rbacEdit ? <UserListActions /> : undefined}
      exporter={exporter}
      hideRemoveButton={!rbac.isSuperadmin()}
      confirmRemoval
      filters={[
        ...Filters,
        <SelectArrayInput
          key="cropIds"
          source="cropIds"
          choices={cropOptions}
          className="MS-field"
          label="Crops"
          style={{ width: 268 }}
        />,
        <SelectArrayInput
          key="stateIds"
          source="stateIds"
          choices={stateOptions}
          className="MS-field"
          label="State"
          style={{ width: 268 }}
        />,
        <SelectArrayInput
          label="Owners"
          key="contactOwnerIds"
          source="contactOwnerIds"
          choices={adminOptions}
          className="MS-field"
          style={{ width: 350 }}
        />,
        <SelectArrayInput
          label="Managers"
          key="responsibleAdminIds"
          source="responsibleAdminIds"
          choices={respManagerOptions}
          className="MS-field"
          style={{ width: 350 }}
        />,
      ]}
      sort={{ field: 'id', order: 'DESC' }}
    >
      <Grid
        rowClick={userRowClick}
        defaultColumns={'code fullName status userType phone email hubspotLink registeredAt isCompany sourceEn buyerTiers'.split(' ')}
        sx={{ '& .column-history': { minWidth: 500 } }}
        className={cls.grid}
      >
        <FunctionField label="ID"
          resource="user"
          source="code"
          sortBy="id"
          render={(record: any) => (
            <Link to={`/user/${record.id}?initial`}>
              <TextField source="code" sortBy="id" />
            </Link>
          )} />
        <FunctionField label="Full Name"
          resource="user"
          source="fullName"
          render={(record: any) => (
            <Link to={`/user/${record.id}?initial`}>
              <TextField source="fullName" />
            </Link>
          )} />
        <FunctionField source="isRenewal" label="Eligible for Renewal" render={(record: any) => (
          <span>{record.isRenewal ? 'Yes' : 'No'}</span>
        )} />
        <FunctionField resource="user" source="lastLoginTry" label="Login try"
          render={(record: any) => {
            const loginTry: LoginTry = record.lastLoginTry;
            if (!loginTry) return null;
            const succeedText = loginTry.isAdmin ? 'Is succeed by admin' : 'Is succeed';

            return (
              <div className={cls.loginTry}>
                <div className={cls.loginTryDate}>
                  {dateFormatter.toDateTime(loginTry.createdAt, true)} | {succeedText}
                </div>
                {loginTry.ip ? (
                  <div>
                    IP: {loginTry.ip}{loginTry.location ? ' | ' + loginTry.location : null}
                  </div>
                ) : null}
              </div>
            );
          }}
        />
        <UserStatusField source="status" label="User status" />
        <FunctionField
          source="userType"
          label="User type"
          render={(record: User) => (
            userTypeOptions.find(option => option.id === record.userType)?.name ?? ''
          )}
        />
        <UserProfileProgress source="profileProgress" label="Profile progress" />
        <TextField source="inactiveReason" label="Inactivity Reason" />
        <TextField source="rejectReason" label="Reason of rejection" sortable={false} />
        <PhoneField source="phone" label="Phone" isMasked={!rbac.canShowUserPhone()} />
        <EmailField source="email" label="Email" wrap />
        <DateField source="createdAt" label="Registration Date" />
        <DateField source="lastStatusAt" label="Last Status Change" showTime />
        <TextField source="sourceEn" label="Channel" />
        <TextField source="gender" label="Gender" />
        <DateField source="birthday" label="Birthday" />
        <TextField source="contactOwner.name" label="Owner" />
        <TextField source="buyerNames" label="Buyers" sortable={false} />
        <TextField source="retailerNames" label="Retailers" sortable={false} />
        <TextField source="associateNames" label="Associates" sortable={false} />
        <ButtonLinkField source="sfContactLink"
          text="SalesForce Contact" label="SalesForce Contact" center isBlue />
        <ButtonLinkField source="sfAccountLink"
          text="SalesForce Account" label="SalesForce Account" center isBlue />
        <TextField source="companyName" label="Company" />
        <BooleanField source="isCompany" label="Is Company" />
        <TextField source="moffinBureauScore" label="Bureau Score" />
        <TextField source="cropsNames" label="Crops" />
        <TextField source="latestCredit.code" label="Latest Credit" />
        <TextField source="latestCredit.statusEn" label="Latest Credit Status" />
        <MoneyField source="latestCredit.requestAmount" label="Last Requested Amount" nullable />
        <MoneyField source="latestCredit.creditAmount" label="Last Credit Amount" nullable />
        <CommentsField
          latest
          source="comments"
          partnerClass="User"
          label="Notes"
          editable={rbacEdit}
          sortable={false}
        />
        <TextField source="mainBuyer.name" label="Withholding Company" />
        <TextField source="tier" label="Withholding Company Tier" />
        {isSuperadmin ? (
          <ReferenceField source="stateId" reference="state" label="State">
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="state.name" label="State" />
        )}
        <TextField source="cityName" label="City" />
        <TextField source="rfc" label="RFC" />
        <TextField source="rfcCompany" label="RFC (Company)" />
        <TextField source="rfcConflictFields" label="RFC Conflicts" />
        <TextField source="marriage" label="Civil status" />
        <TextField source="address" label="Address" sortable={false} />
        <UserCity source="city" label="Municipality" sortable={false} />
        <TextField source="activation.activation" label="Activation" />
        {isSuperadmin ? (
          <ReferenceField reference="admin" source="responsibleAdminId" label="Manager">
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="responsibleAdmin.name" label="Responsible manager" />
        )}
        {showLoginButton ? <UserLoginBtn source="login" label="Login by User" /> : null}
        <ImageField source="avatar" width={100} round label="Photo" />
        <TextField source="timerText" label="Register to Applying" />
        <SyntageStatusField source="syntageProfile.credentialStatus" label="CIEC Status" />
        <SyntageProfileField source="syntageProfile" label="SAT Profile" />
        <DateField source="syntageProfile.createdAt" label="SAT Timestamp" showTime />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput key="q" source="q" label="Search" alwaysOn style={{ width: 220 }} />,
  <TextInput key="buyer" source="buyer" label="Buyer" style={{ width: 240 }} />,
  <AutocompleteFilter
    key="activationId"
    source="activationId"
    reference="finance-activation"
    label="Activation"
    style={{ width: 240 }}
  />,
  <TextInput key="retailer" source="retailer" label="Retailer" style={{ width: 240 }} />,
  <TextInput key="associate" source="associate" label="Associate" style={{ width: 240 }} />,
  <TextInput key="ids" source="ids" label="User IDs" style={{ width: 240 }} />,
  <SelectInput
    key="status"
    source="status"
    label="User status"
    placeholder="User status"
    choices={userStatusOptions}
    alwaysOn
  />,
  <SelectInput key="profileProgress" source="profileProgress" choices={profileProgressOptions} />,
  <SelectInput key="gender" source="gender" choices={genderOptions} />,
  <SelectInput key="userType" source="userType" label="User type" choices={userTypeOptions} />,
  <SelectInput key="loginTry" source="loginTry" choices={loginTryOptions} />,
  <SelectInput key="marriage" source="marriage" choices={marriageOptions} label="Civil status" />,
  <DateRangeSelect
    key="createdAt"
    source="createdAt"
    label="Registration/Last Status Change"
    alwaysOn
    choices={[
      { id: 'registeredAt', name: 'Registration Date' },
      { id: 'lastStatusAt', name: 'Last Status Change' },
    ]}
  />,
  <TextInput key="city" source="city" label="Municipality" style={{ width: 180 }} />,
  <SelectArrayInput
    style={{ width: 180 }}
    key="tier"
    source="tier"
    label="Withholding Company Tier"
    choices={dealerTiersChoices}
    className={'MS-field'}
  />,
  <TextInput key="mainBuyer"
    source="mainBuyer"
    label="Withholding Company"
    style={{ width: 270 }} />,
  <SelectArrayInput
    key="sourceEn"
    source="sourceEn"
    label="Channel"
    choices={userSourceOptions}
    className="MS-field"
    style={{ width: 350 }}
  />,
  <SelectArrayInput
    key="latestCreditStatus"
    source="latestCreditStatus"
    label="Latest Credit Status"
    choices={creditStatusBlankOptions}
    className="MS-field"
    style={{ width: 350 }}
  />,
  <NullableBooleanInput key="isRenewal" source="isRenewal" label="Eligible for Renewal" />,
  <SelectInput key="moffin" source="moffin" label="Moffin Profile" choices={moffinOptions} />,
  <NullableBooleanInput key="isDemo" source="isDemo" label="Show Demo" />,
];
